import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
} from '@mui/material';
import {
  Person,
  Telegram,
  Language,
  CalendarToday,
  Star,
  ExitToApp,
  Refresh,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { normalizeImageUrl } from '../utils/imageUtils';

const ProfilePage: React.FC = () => {
  const { user, logout, isLoading, resetOnboarding } = useAuthStore();
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/');
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleResetOnboarding = async () => {
    await resetOnboarding();
    // Перезагрузить страницу, чтобы показать онбординг
    window.location.href = '/';
  };

  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box textAlign="center">
          <Typography variant="h6">Пользователь не найден</Typography>
        </Box>
      </Container>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      {/* Header with back button */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 3,
      }}>
        <Button
          onClick={handleGoBack}
          sx={{ 
            minWidth: 48, 
            width: 48, 
            height: 48, 
            color: '#000000',
            p: 0,
            mr: 2
          }}
        >
          <ArrowBack />
        </Button>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#000000' }}>
          Профиль
        </Typography>
      </Box>

      {/* Profile Header */}
      <Card elevation={0} sx={{ mb: 3, bgcolor: 'rgb(248, 242, 255)' }}>
        <CardContent sx={{ textAlign: 'center', pb: 2 }}>
          <Avatar
            src={normalizeImageUrl(user.photo_url)}
            sx={{
              width: 100,
              height: 100,
              mx: 'auto',
              mb: 2,
              border: '4px solid',
              borderColor: 'primary.main',
            }}
          >
            <Person sx={{ fontSize: 50 }} />
          </Avatar>
          
          <Typography variant="h5" gutterBottom fontWeight="bold">
            {user.full_name}
          </Typography>
          
          {user.username && (
            <Typography variant="body1" color="text.secondary" gutterBottom>
              @{user.username}
            </Typography>
          )}
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
            {user.is_premium && (
              <Chip
                icon={<Star />}
                label="Premium"
                color="primary"
                size="small"
              />
            )}
            <Chip
              icon={<Telegram />}
              label="Telegram"
              variant="outlined"
              size="small"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Profile Details */}
      <Card elevation={0} sx={{ mb: 3, bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Информация о профиле
          </Typography>
          
          <List disablePadding>
            <ListItem>
              <ListItemIcon>
                <Person />
              </ListItemIcon>
              <ListItemText
                primary="Имя"
                secondary={user.display_name}
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            <ListItem>
              <ListItemIcon>
                <Language />
              </ListItemIcon>
              <ListItemText
                primary="Язык"
                secondary={user.language_code === 'ru' ? 'Русский' : user.language_code}
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            <ListItem>
              <ListItemIcon>
                <CalendarToday />
              </ListItemIcon>
              <ListItemText
                primary="Дата регистрации"
                secondary={formatDate(user.created_at)}
              />
            </ListItem>
            
            {user.last_active_at && (
              <>
                <Divider variant="inset" component="li" />
                <ListItem>
                  <ListItemIcon>
                    <CalendarToday />
                  </ListItemIcon>
                  <ListItemText
                    primary="Последняя активность"
                    secondary={formatDate(user.last_active_at)}
                  />
                </ListItem>
              </>
            )}
          </List>
        </CardContent>
      </Card>

      {/* Telegram Info */}
      <Card elevation={0} sx={{ mb: 3, bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Telegram
          </Typography>
          
          <List disablePadding>
            <ListItem>
              <ListItemIcon>
                <Telegram />
              </ListItemIcon>
              <ListItemText
                primary="Telegram ID"
                secondary={user.telegram_id}
              />
            </ListItem>
            
            {user.is_premium && (
              <>
                <Divider variant="inset" component="li" />
                <ListItem>
                  <ListItemIcon>
                    <Star />
                  </ListItemIcon>
                  <ListItemText
                    primary="Telegram Premium"
                    secondary="Активная подписка"
                  />
                </ListItem>
              </>
            )}
          </List>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card elevation={0} sx={{ bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Действия
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleResetOnboarding}
            fullWidth
            sx={{ 
              mb: 2,
              borderColor: '#283655',
              color: '#000000',
              '&:hover': {
                borderColor: '#283655',
                backgroundColor: 'rgba(40, 54, 85, 0.04)'
              }
            }}
          >
            Повторно пройти онбординг
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<ExitToApp />}
            onClick={handleLogout}
            disabled={isLoading}
            fullWidth
            sx={{
              borderColor: '#283655',
              color: '#000000',
              '&:hover': {
                borderColor: '#283655',
                backgroundColor: 'rgba(40, 54, 85, 0.04)'
              }
            }}
          >
            {isLoading ? 'Выход...' : 'Выйти из аккаунта'}
          </Button>
          
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ display: 'block', mt: 1, textAlign: 'center' }}
          >
            При выходе вам потребуется повторная авторизация через Telegram
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ProfilePage; 